import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/DatePicker";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  apiGetMyOffboardingRequest,
  apiCreateOffboardingRequest,
  apiRevokeOffboardingRequest,
} from "@/lib/api";
import { EmployeeProfile } from "@/data/employees";

interface Props {
  employee: EmployeeProfile;
  isOwnProfile?: boolean;
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending_manager: "secondary",
  pending_hr: "secondary",
  active: "default",
  completed: "default",
  rejected: "destructive",
  revoked: "outline",
};

const STATUS_LABELS: Record<string, string> = {
  pending_manager: "Pending Manager Review",
  pending_hr: "Pending HR Review",
  active: "Active",
  completed: "Completed",
  rejected: "Rejected",
  revoked: "Revoked",
};

export function ProfileExitTab({ employee, isOwnProfile }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [preferredDate, setPreferredDate] = useState<string>("");
  const [reason, setReason] = useState("");
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revocationReason, setRevocationReason] = useState("");

  const { data: request, isLoading } = useQuery({
    queryKey: ["my-offboarding-request"],
    queryFn: apiGetMyOffboardingRequest,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!preferredDate) throw new Error("Please select a date.");
      return apiCreateOffboardingRequest(Number(employee.id), preferredDate, reason);
    },
    onSuccess: () => {
      toast.success("Request submitted successfully.");
      queryClient.invalidateQueries({ queryKey: ["my-offboarding-request"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to submit request.");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: number) => apiRevokeOffboardingRequest(id),
    onSuccess: () => {
      toast.success("Request revoked successfully.");
      setShowRevokeModal(false);
      setRevocationReason("");
      queryClient.invalidateQueries({ queryKey: ["my-offboarding-request"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to revoke request.");
    },
  });

  if (isLoading) return <div className="p-4 text-sm">{t("common.loading")}</div>;

  if (request) {
    // Date-based gate: can revoke if not already revoked, AND either no last day set or today is on/before it
    const canRevoke = request.status !== "revoked" && (() => {
      if (!request.actual_last_day) return true;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const lastDay = new Date(request.actual_last_day); lastDay.setHours(0, 0, 0, 0);
      return today <= lastDay;
    })();
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("offboardingModule.statusTracker")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground mb-1">{t("common.status")}</p>
                <Badge variant={STATUS_VARIANT[request.status] ?? "secondary"}>
                  {STATUS_LABELS[request.status] ?? request.status}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">{t("offboardingModule.preferredLastDay")}</p>
                <p className="font-medium text-foreground">{request.preferred_last_day?.split("T")[0]}</p>
              </div>
              {request.actual_last_day && (
                <div>
                  <p className="text-muted-foreground mb-1">{t("offboardingModule.actualLastDay")}</p>
                  <p className="font-medium text-foreground">{request.actual_last_day.split("T")[0]}</p>
                </div>
              )}
              {request.manager_notes && (
                <div className="col-span-2">
                  <p className="text-muted-foreground mb-1">{t("offboardingModule.managerNotes")}</p>
                  <p className="font-medium bg-muted p-3 rounded-md">{request.manager_notes}</p>
                </div>
              )}
              {request.hr_notes && (
                <div className="col-span-2">
                  <p className="text-muted-foreground mb-1">{t("offboardingModule.hrNotes")}</p>
                  <p className="font-medium bg-muted p-3 rounded-md">{request.hr_notes}</p>
                </div>
              )}
            </div>

            {canRevoke && (
              <div className="pt-2 flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowRevokeModal(true)}
                >
                  Revoke Request
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revoke Confirmation Modal */}
        <Dialog open={showRevokeModal} onOpenChange={(v) => { if (!v) { setShowRevokeModal(false); setRevocationReason(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke Exit Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to revoke your exit request? This will notify your Line Manager and HR.
              </p>
              <div className="space-y-2">
                <Label>Reason for Revocation (optional)</Label>
                <Textarea
                  placeholder="Explain why you are revoking this request..."
                  value={revocationReason}
                  onChange={(e) => setRevocationReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRevokeModal(false); setRevocationReason(""); }}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => revokeMutation.mutate(request.id)}
                disabled={revokeMutation.isPending}
              >
                {revokeMutation.isPending ? t("common.saving") : "Yes, Revoke"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (!isOwnProfile) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("offboardingModule.requestOffboarding")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>{t("offboardingModule.preferredLastDay")}</Label>
          <DatePicker value={preferredDate} onChange={setPreferredDate} disablePastDates />
        </div>
        <div className="space-y-2">
          <Label>{t("offboardingModule.reason")}</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} />
        </div>
        <Button
          onClick={() => submitMutation.mutate()}
          disabled={!preferredDate || submitMutation.isPending}
          className="gradient-brand text-white"
        >
          {submitMutation.isPending ? t("offboardingModule.submitting") : t("offboardingModule.submitRequest")}
        </Button>
      </CardContent>
    </Card>
  );
}
